import { useState, useEffect, forwardRef } from "react";
import { X } from "lucide-react";
import Button from "./Button";
import SearchableSelect from "../Controls/SearchableSelect";
import { useTranscriptionStore } from "../../store";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./datepicker-purple-theme.css";

const CustomDateInput = forwardRef(({ value, onClick, placeholder, onChange }, ref) => (
    <input
        type="text"
        value={value ?? ""}
        onClick={onClick}
        onChange={onChange}
        ref={ref}
        className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all"
        placeholder={placeholder}
        readOnly
    />
));

export default function NewCaseModal({
    onClose,
    onCreate,
    initialData = null,
}) {
    const isEdit = !!initialData?._id;

    const {
        clients,
        radiologists,
        modalities,
        scribes,
        addError,
        currentSession,
        updateDataByField,
        updateMultipleFields,
        techniques,
        userRole,
        userId,
        bodyParts,
        getBodyParts,
        getAllClients,
        getAllRadiologists,
        getAllScribes
    } = useTranscriptionStore();

    // Form state
    const [name, setCaseName] = useState("");
    const [accession_No, setAccessionNumber] = useState("");
    const [patient, setPatientName] = useState("");
    const [gender, setGender] = useState("");
    const [type_of_study, setTypeOfStudy] = useState("");
    const [technique, setTechnique] = useState("");
    const [bodypart, setBodyPart] = useState("");
    const [history, setHistory] = useState("");
    const [comparison, setComparison] = useState("None");
    const [comparisonHistory, setComparisonHistory] = useState(""); // New field
    const [mva, setMva] = useState(false);
    const [stir, setStir] = useState(false);
    const [contrast_image, setContrastImage] = useState(false);
    const [weightbearing, setWeightbearing] = useState(false);
    const [infinity, setInfinity] = useState(false);
    const [client, setClientId] = useState("");
    const [radiologist, setRadiologistId] = useState("");
    const [scribe, setScribeId] = useState("");
    const [modality, setModalityId] = useState("");
    const [notes, setAdditionalNotes] = useState("");
    const [deadline_minutes, setDeadlineMinutes] = useState(null);
    const [dateOfService, setDateofService] = useState("");
    const [dateOfBirth, setDateofBirth] = useState("");
    const [fieldErrors, setFieldErrors] = useState({});

    const SPINE_REGIONS = ["cervical", "thoracic", "lumbar"];
    const selectedBodyPartName =
        bodyParts.find((p) => p._id === bodypart)?.body_part || "";
    const isSpine = SPINE_REGIONS.some((r) =>
        selectedBodyPartName.toLowerCase().includes(r)
    );

    useEffect(() => {
        if (!isEdit || !initialData) return;

        setCaseName(initialData?.name || "");
        setAccessionNumber(initialData?.accession_No || "");
        setPatientName(initialData?.patient || "");
        setGender(initialData?.patient_gender || "");
        setTypeOfStudy(initialData?.type_of_study || "");
        setTechnique(initialData?.technique || "");
        setBodyPart(initialData?.bodypart || "");
        setHistory(initialData?.history || "");
        setComparison(initialData?.comparison || "None");
        setComparisonHistory(initialData?.comparison_report || ""); // Load comparison history
        setMva(Boolean(initialData?.mva));
        setStir(Boolean(initialData?.stir));
        setContrastImage(Boolean(initialData?.contrast_image));
        setWeightbearing(Boolean(initialData?.weightbearing));
        setInfinity(Boolean(initialData?.infinity));

        // Extracting _id from nested objects
        setClientId(initialData?.client?._id || "");
        setRadiologistId(initialData?.radiologist?._id || "");
        setScribeId(initialData?.scribe?._id || "");
        setModalityId(initialData?.modality?._id || "");

        setAdditionalNotes(initialData?.notes || "");
        setDeadlineMinutes(
            initialData?.deadline_minutes !== undefined &&
                initialData?.deadline_minutes !== null
                ? Number(initialData.deadline_minutes)
                : null
        );
        setDateofService(initialData?.date_of_service || "");
        setDateofBirth(initialData?.patient_dob || "");
    }, [isEdit, initialData]);


    // Auto-select scribe if user role is SCRIBE (for both create and edit)
    useEffect(() => {
        if (userRole === 'SCRIBE' && userId) {
            // Check if userId exists in scribes options
            const isValidScribe = scribes.some(s => s.value === userId);
            if (isValidScribe && scribe !== userId) {
                setScribeId(userId);
            }
        }
    }, [userRole, userId, scribes, scribe]);

    // Clear error when field value changes
    const clearFieldError = (fieldName) => {
        if (fieldErrors[fieldName]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[fieldName];
                return newErrors;
            });
        }
    };

    // Wrapper setters that clear errors
    const handleSetAccessionNumber = (val) => { setAccessionNumber(val); clearFieldError('accession_No'); };
    const handleSetPatientName = (val) => { setPatientName(val); clearFieldError('patient'); };
    const handleSetGender = (val) => { setGender(val); clearFieldError('gender'); };
    const handleSetModality = (val) => { setModalityId(val); clearFieldError('modality'); setTechnique(""); };
    const handleSetTypeOfStudy = (val) => { setTypeOfStudy(val); clearFieldError('type_of_study'); };
    const handleSetTechnique = (val) => { setTechnique(val); clearFieldError('technique'); };
    const handleSetBodyPart = (val) => {
        setBodyPart(val);
        clearFieldError('bodypart');
        clearFieldError('type_of_study');
        clearFieldError('technique');
    }; const handleSetClient = (val) => { setClientId(val); clearFieldError('client'); };

    const getOnlyChangedFields = (newData, oldData) => {
        const changed = {};

        Object.entries(newData).forEach(([key, newVal]) => {
            let oldVal = oldData?.[key];

            // If backend sends nested objects (client: { _id, name, ... })
            if (oldVal && typeof oldVal === "object" && oldVal._id) {
                oldVal = oldVal._id;
            }

            // 🔹 Special handling for deadline_minutes (number comparison)
            if (key === "deadline_minutes") {
                const normNew =
                    newVal === "" || newVal === undefined || newVal === null
                        ? null
                        : Number(newVal);

                const normOld =
                    oldVal === "" || oldVal === undefined || oldVal === null
                        ? null
                        : Number(oldVal);

                if (normNew !== normOld) {
                    changed[key] = newVal; // keep original (null or number)
                }

                return; // done for this key
            }

            // 🔹 Special handling for string-ish fields that can be ""
            if (["date_of_service", "patient_dob", "comparison"].includes(key)) {
                const normNew = newVal === "None" ? "" : (newVal ?? "");
                const normOld = oldVal ?? "";

                if (normNew !== normOld) {
                    changed[key] = normNew;
                }
                return;
            }


            if (newVal !== oldVal) {
                changed[key] = newVal;
            }
        });

        return changed;
    };

    const handleSubmit = async () => {
        // Fields to validate (excluding `patient`)
        let mandatoryFields = {};

        if (userRole === 'RADIOLOGIST') {
            mandatoryFields = { accession_No };
        } else {
            mandatoryFields = {
                accession_No,
                patient,
                modality,
                bodypart,
                client,
                history,
                patient_gender: gender,
                patient_dob: dateOfBirth
            };
        }

        const missingFields = Object.entries(mandatoryFields).filter(
            ([, value]) => value === undefined || value === null || value === ""
        );

        if (missingFields.length > 0) {
            // Set error state for missing fields
            const errors = {};
            missingFields.forEach(([key]) => {
                errors[key] = true;
            });
            setFieldErrors(errors);

            const missingFieldNames = missingFields.map(([key]) => key).join(", ");
            addError("warning", `Please fill in all mandatory fields: ${missingFieldNames}`);
            return;
        }
        // Clear all errors if validation passes
        setFieldErrors({});

        if (currentSession?._id) {
            const updatePayload = {
                ...mandatoryFields,
                name,
                comparison: comparison === "None" ? "" : comparison,
                comparison_report: comparison !== "None" ? comparisonHistory : "",
                mva,
                stir,
                type_of_study,
                technique,
                weightbearing,
                infinity,
                contrast_image,
                radiologist,
                scribe,
                notes,
                deadline_minutes,
                date_of_service: dateOfService,
                patient_dob: dateOfBirth
            };

            const changedFields = getOnlyChangedFields(updatePayload, initialData);

            // If no changes detected, just close the modal silently
            if (Object.keys(changedFields).length === 0) {
                onClose();
                return;
            }

            try {
                if (Object.keys(changedFields).length === 1) {
                    const [field, value] = Object.entries(changedFields)[0];
                    await updateDataByField(field, value);
                } else {
                    await updateMultipleFields(changedFields);
                }
            } catch (e) {
                console.error("Failed to update case fields", e);
                addError("warning", "Failed to update case fields");
            }

            onClose();
        } else {
            // New case creation
            onCreate({
                ...mandatoryFields,
                name,
                comparison,
                comparison_history: comparison ? comparisonHistory : "", // Only include if comparison has date
                mva,
                type_of_study,
                technique,
                stir,
                contrast_image,
                weightbearing,
                infinity,
                radiologist,
                scribe,
                notes,
                deadline_minutes,
                date_of_service: dateOfService,
                patient_dob: dateOfBirth
            });
            onClose();
        }
    };

    const parseDate = (value) => {
        if (!value) return undefined;
        const date = new Date(value);
        return isNaN(date.getTime()) ? undefined : date;
    };

    const typeOfStudyOptions = [
        { label: "MVA", value: "mva" },
        { label: "Non-MVA", value: "non-mva" }
    ];

    // Helper to get input class with error state
    const getInputClass = (fieldName) => {
        const baseClass = "w-full px-3 py-2 rounded-lg bg-black/30 border text-white placeholder-white/40 focus:outline-none focus:ring-2 transition-all";
        // if (fieldErrors[fieldName]) {
        //     return `${baseClass} border-red-500 focus:ring-red-500/50 focus:border-red-400`;
        // }
        return `${baseClass} border-white/20 focus:ring-purple-500/50 focus:border-purple-400`;
    };

    // Map field labels to their state keys for error checking
    const fieldKeyMap = {
        "Accession Number": "accession_No",
        "Patient Name": "patient",
        "Modality": "modality",
        "Body Part": "bodypart",
        "Type of Study": "type_of_study",
        "Technique": "technique",
        "History": "history",
        "Patient Gender": "patient_gender",
        "Patient DOB": "patient_dob",
    };

    // Map for setter functions that clear errors
    const errorClearingSetters = {
        "Accession Number": handleSetAccessionNumber,
        "Patient Name": handleSetPatientName,
        "Modality": handleSetModality,
        "Body Part": handleSetBodyPart,
        "Type of Study": handleSetTypeOfStudy,
        "Technique": handleSetTechnique,
    };

    const formatDateInput = (value) => {
        const digits = value.replace(/\D/g, "").slice(0, 8);

        if (digits.length <= 2) return digits;
        if (digits.length <= 4) return `${digits.slice(0, 2)}-${digits.slice(2)}`;
        return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
    };

    const formatComparisonInput = (value) => {
        // allow empty while typing
        if (value === "") return "";

        // allow typing "none"
        if (/^[a-zA-Z]+$/.test(value)) {
            if (value.toLowerCase() === "none") return "None";
            return value; // allow typing n, no, non
        }

        // otherwise, date only
        return formatDateInput(value);
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-sm">
            <div className="border border-white/20 scrollbar-thin scrollbar-track-slate-800 scrollbar-thumb-slate-600 rounded-2xl bg-black/70 shadow-2xl max-w-3xl w-full mx-4 max-h-[95vh] overflow-y-auto">
                <div className="p-6">
                    {/* Header */}
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-xl font-semibold text-white">
                            {isEdit ? "Edit Case Details" : "Create New Case"}
                        </h2>
                        <Button variant="ghost" size="sm" onClick={onClose} className="text-white/70 hover:text-white">

                            {/* <Button variant="ghost" size="sm" onClick={handleSubmit} className="text-white/70 hover:text-white"> */}
                            <X size={18} />
                        </Button>
                    </div>

                    {/* Form */}
                    <div className="space-y-6 text-sm">
                        {/* Row 1: 7 inputs */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                { label: "Title", value: name, setter: setCaseName },
                                { label: "Accession Number", value: accession_No, setter: handleSetAccessionNumber, required: true },
                                { label: "Patient Name", value: patient, setter: handleSetPatientName, required: true },
                                {
                                    label: "Modality",
                                    value: modality,
                                    setter: handleSetModality,
                                    type: "searchable-select",
                                    options: modalities,
                                    required: true
                                },
                                {
                                    label: "Body Part",
                                    value: bodypart,
                                    setter: handleSetBodyPart,
                                    type: "searchable-select",
                                    options: bodyParts.map((part) => ({
                                        value: part._id,
                                        label: part.body_part,
                                    })),
                                    required: true,
                                    callFun: getBodyParts
                                },
                                {
                                    label: "Type of Study",
                                    value: type_of_study,
                                    setter: handleSetTypeOfStudy,
                                    type: "searchable-select",
                                    options: typeOfStudyOptions,
                                    required: isSpine
                                },
                                {
                                    label: "Technique",
                                    value: technique,
                                    setter: handleSetTechnique,
                                    type: "searchable-select",
                                    options: techniques
                                        .filter((i) => !modality || i.modalityId?._id === modality)
                                        .map((i) => ({
                                            value: i._id,
                                            label: i.name
                                        })),
                                    required: isSpine
                                },
                                { label: "TAT(Turn Around time) (in minutes)", type: 'number', value: deadline_minutes, setter: setDeadlineMinutes },
                                { label: "Comparison", type: "input", value: comparison, setter: setComparison },
                                { label: "Date of Service", type: "input", value: dateOfService, setter: setDateofService },
                                { label: "Patient DOB", type: "input", value: dateOfBirth, setter: setDateofBirth, required: true },
                                {
                                    label: "Patient Gender",
                                    value: gender,
                                    setter: handleSetGender,
                                    type: "gender-checkbox",
                                    required: true
                                },
                                { label: "History", value: history, setter: setHistory, required: true },
                            ]
                                .filter(({ label }) => !(isEdit && (label === "Case Name" || label === "Accession Number")))
                                .map(({ label, value, setter, type, options, required, callFun }, idx) => {
                                    const fieldKey = fieldKeyMap[label];
                                    const hasError = fieldKey && fieldErrors[fieldKey];

                                    return (
                                        <div key={idx} className={label === "History" ? "md:col-span-2" : ""}>
                                            <label className="block text-white/80 mb-1">
                                                {label} {required && <span className="text-red-400">*</span>}
                                            </label>

                                            {type === "gender-checkbox" ? (
                                                <div className={`flex items-center gap-6 py-2`}>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={value === "Male"}
                                                            onChange={() => setter(value === "Male" ? "" : "Male")}
                                                            className="form-checkbox h-4 w-4 text-purple-600 accent-purple-800 rounded"
                                                        />
                                                        <span className="text-white/80">Male</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={value === "Female"}
                                                            onChange={() => setter(value === "Female" ? "" : "Female")}
                                                            className="form-checkbox h-4 w-4 text-purple-600 accent-purple-800 rounded"
                                                        />
                                                        <span className="text-white/80">Female</span>
                                                    </label>
                                                    <label className="flex items-center gap-2 cursor-pointer">
                                                        <input
                                                            type="checkbox"
                                                            checked={value === "Other"}
                                                            onChange={() => setter(value === "Other" ? "" : "Other")}
                                                            className="form-checkbox h-4 w-4 text-purple-600 accent-purple-800 rounded"
                                                        />
                                                        <span className="text-white/80">Others</span>
                                                    </label>
                                                </div>
                                            ) : type === "searchable-select" ? (
                                                <div>
                                                    <SearchableSelect
                                                        value={value}
                                                        onChange={(selected) => setter(selected)}
                                                        options={options}
                                                        placeholder={`Select ${label}...`}
                                                        callFun={callFun}
                                                    />
                                                </div>
                                            ) : label === "History" ? (
                                                <textarea
                                                    value={value}
                                                    onChange={(e) => setter(e.target.value)}
                                                    rows={3}
                                                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all"
                                                />
                                            ) : type === "date" ? (
                                                <div className="w-full custom-datepicker">
                                                    <DatePicker
                                                        selected={parseDate(value)}
                                                        onChange={(date) => {
                                                            if (date) {
                                                                const yyyy = date.getFullYear();
                                                                const mm = String(date.getMonth() + 1).padStart(2, "0");
                                                                const dd = String(date.getDate()).padStart(2, "0");
                                                                setter(`${yyyy}-${mm}-${dd}`);
                                                            } else {
                                                                setter("");
                                                            }
                                                        }}
                                                        dateFormat="MM/dd/yyyy"
                                                        placeholderText="MM/DD/YYYY"
                                                        customInput={<CustomDateInput placeholder="MM/DD/YYYY" />}
                                                        wrapperClassName="w-full"
                                                        isClearable
                                                    />
                                                </div>
                                            ) : (
                                                <input
                                                    type="text"
                                                    value={value ?? ""}
                                                    onChange={(e) => {
                                                        const raw = e.target.value;

                                                        //  Comparison: none OR date
                                                        if (label === "Comparison") {
                                                            setter(formatComparisonInput(raw));
                                                        }

                                                        //  DOB & DOS: date only
                                                        else if (
                                                            label === "Patient DOB" ||
                                                            label === "Date of Service"
                                                        ) {
                                                            setter(formatDateInput(raw));
                                                        }

                                                        //  TAT: number only
                                                        else if (type === "number") {
                                                            setter(raw === "" ? null : raw);
                                                        }

                                                        //  everything else
                                                        else {
                                                            setter(raw);
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        // enforce None only for Comparison
                                                        if (label === "Comparison" && (!value || value.trim() === "")) {
                                                            setter("None");
                                                        }
                                                    }}
                                                    placeholder={
                                                        label === "Comparison"
                                                            ? "None or MM-DD-YYYY"
                                                            : (label === "Patient DOB" || label === "Date of Service")
                                                                ? "MM-DD-YYYY"
                                                                : label === "TAT(Turn Around time) (in minutes)"
                                                                    ? "Enter minutes..."
                                                                    : `Enter ${label}...`
                                                    }
                                                    maxLength={label === "Comparison" ? 10 : undefined}
                                                    className={getInputClass(fieldKey)}
                                                />

                                            )}

                                            {/* Error message below field */}
                                            {hasError && (
                                                <p className="text-red-400 text-xs mt-1">This field is required</p>
                                            )}
                                        </div>
                                    );
                                })}

                            {/* Comparison History - Only shown when comparison date is selected */}
                            {comparison && comparison !== 'None' && (
                                <div className="md:col-span-2">
                                    <label className="block text-white/80 mb-1">Comparison History</label>
                                    <textarea
                                        value={comparisonHistory}
                                        onChange={(e) => setComparisonHistory(e.target.value)}
                                        rows={isEdit ? 9 : 4}
                                        placeholder=""
                                        className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all"
                                    />
                                </div>
                            )}
                        </div>

                        {/* Row 2: Dropdowns */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-white/80 mb-1">Client<span className="text-red-400">*</span></label>
                                <div>
                                    <SearchableSelect
                                        value={client}
                                        onChange={(e) => handleSetClient(e)}
                                        options={clients}
                                        placeholder="Client..."
                                        callFun={getAllClients}
                                    />
                                </div>
                                {fieldErrors.client && (
                                    <p className="text-red-400 text-xs mt-1">This field is required</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-white/80 mb-1">Radiologist</label>
                                <SearchableSelect
                                    value={radiologist}
                                    onChange={(e) => setRadiologistId(e)}
                                    options={radiologists}
                                    placeholder="Radiologist..."
                                    callFun={getAllRadiologists}
                                />
                            </div>

                            <div>
                                <label className="block text-white/80 mb-1">
                                    Scribe
                                    {userRole === 'SCRIBE' && (
                                        <span className="ml-2 text-xs text-green-400">(Auto-selected)</span>
                                    )}
                                </label>
                                <SearchableSelect
                                    value={scribe}
                                    onChange={(e) => setScribeId(e)}
                                    options={scribes}
                                    placeholder="Scribes..."
                                    disabled={userRole === 'SCRIBE'}
                                    callFun={getAllScribes}
                                />
                            </div>
                        </div>

                        {/* Row 3: Toggles */}
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            {[
                                { label: "STIR", checked: stir, setter: setStir },
                                { label: "Contrast Image", checked: contrast_image, setter: setContrastImage },
                                { label: "Weightbearing", checked: weightbearing, setter: setWeightbearing },
                                { label: "Infinity", checked: infinity, setter: setInfinity }
                            ].map(({ label, checked, setter }, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={(e) => setter(e.target.checked)}
                                        className="form-checkbox text-purple-600 accent-purple-800"
                                    />
                                    <label className="text-white/80">{label}</label>
                                </div>
                            ))}
                        </div>

                        {/* Row 4: Additional Notes */}
                        {!isEdit && (
                            <div>
                                <label className="block text-white/80 mb-1">Additional Notes</label>
                                <textarea
                                    value={notes}
                                    onChange={(e) => setAdditionalNotes(e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-purple-400 transition-all"
                                />
                            </div>
                        )}
                    </div>

                    {/* Footer Buttons */}
                    <div className="flex justify-end gap-3 mt-3">
                        <Button variant="ghost" size="sm" onClick={onClose} className="text-white/70 hover:text-white">
                            {/* <Button variant="ghost" size="sm" onClick={handleSubmit} className="text-white/70 hover:text-white"> */}
                            Cancel
                        </Button>
                        <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={handleSubmit}>
                            {isEdit ? "Save Changes" : "Create Case"}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}